import { Capacitor } from '@capacitor/core';
import { BarcodeFormat, BarcodeScanner } from '@capacitor-mlkit/barcode-scanning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DnsIcon from '@mui/icons-material/Dns';
import SensorsIcon from '@mui/icons-material/Sensors';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormHelperText,
  InputAdornment,
  TextField,
  ToggleButton,
} from '@mui/material';
import { green, orange } from '@mui/material/colors';
import { useFormik } from 'formik';
import _ from 'lodash';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';

import { dispatchUnitActions } from '@sophocles/api/odlApp';
import { fetchRadio, resolveRadios } from '@sophocles/api/radios';
import { useUser } from '@sophocles/app';
import ResponsiveDialog from '@sophocles/components/ResponsiveDialog';
import { fetchKoreSubscriptionByICCID } from '@sophocles/entities/KoreSubscription/actions';
import { createRadio } from '@sophocles/entities/Radio';
import { changeSiteDevice } from '@sophocles/entities/Site';
import { selectCapabilities } from '@sophocles/features/capabilities';
import { selectSiteData, selectSiteUnit } from '@sophocles/features/entities/site';
import { loadRadioLocations } from '@sophocles/features/inventory/radioLocations/api';
import { selectTenantInfo } from '@sophocles/features/tenant/slice';
import { addToast } from '@sophocles/features/toast';
import useSiteGroupTerm from '@sophocles/hooks/useSiteGroupTerm';
import { determineIsTechSupport } from '@sophocles/utils';
import { parseApiError } from '@sophocles/utils/apiErrors';
import {
  allowedIPAddress,
  allowedIPAddressCharacters,
  determineIPAddressType,
  validMACAddress,
} from '@sophocles/utils/validation';
import RadioChangeConfirmationDialog from '@sophocles/views/sites/components/UnitCard/RadioChangeConfirmationDialog';
import RadioWarningDialog from '@sophocles/views/sites/components/UnitCard/RadioWarningDialog';
import {
  buildRadioSwapWarnings,
  deriveRadioAllocation,
} from '@sophocles/views/sites/components/UnitCard/radioSwapWarnings';
import { findResolveMatch } from '@sophocles/views/sites/components/UnitCard/resolveMatch';

// Sentinel returned by the resolve step when the entered ICCID/MAC is valid but
// has no Radio row yet (and the user may create one) — flips the dialog into
// create-and-assign mode instead of erroring.
const CREATE_NEW_RADIO = Symbol('createNewRadio');

function ChangeUnitDialog({ open, onClose, edit, triggerScanOnOpen = false, siteId }) {
  const dispatch = useDispatch();

  const { t } = useTranslation();
  const siteGroupTerm = useSiteGroupTerm();
  const { customer_term: customerTerm } = useSelector(selectTenantInfo);

  const isNativePlatform = Capacitor.isNativePlatform();

  const { user } = useUser();
  const capabilities = useSelector(selectCapabilities);
  const site = useSelector(selectSiteData(siteId));
  const unit = useSelector(selectSiteUnit(siteId)) ?? {};
  const siteGroupId = site?.owner?.id ?? null;
  const unitIccid = unit.radio?.iccid ?? null;
  const unitMac = unit.radio?.mac_address ?? null;
  const hasExistingRadio = unit.radio != null;

  const [title, setTitle] = useState(edit ? t('sites.changeUnit') : t('sites.addDevice'));
  const [loading, setLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [unitRadio, setUnitRadio] = useState(unit.radio?.id ?? null);
  const [creatingNewRadio, setCreatingNewRadio] = useState(false);
  const [iccid, setICCID] = useState(unitIccid ?? unitMac ?? '');
  const [iccidInfo, setICCIDInfo] = useState(null);
  const [iccidSiteGroupName, setICCIDSiteGroupName] = useState(null);
  const [iccidCustomerName, setICCIDCustomerName] = useState(null);
  const [iccidValidated, setICCIDValidated] = useState(false);
  const [unitStatus, setUnitStatus] = useState('Unknown');
  const [fetchingIP, setFetchingIP] = useState(false);
  const [ipAddress, setIPAddress] = useState(unit.radio?.ip_address ?? '');
  const [ipAddressType, setIPAddressType] = useState('IPv4/IPv6');
  const [ipAddressChanged, setIPAddressChanged] = useState(false);
  const [adminOverrideIP, setAdminOverrideIP] = useState(false);
  const [iccidOptions, setICCIDOptions] = useState([]);

  // State for confirmation dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [radioAction, setRadioAction] = useState('inventory');
  const [pendingSubmitValues, setPendingSubmitValues] = useState(null);
  const [currentRadioInfo, setCurrentRadioInfo] = useState(null);
  const [newRadioInfo, setNewRadioInfo] = useState(null);
  // Where the entered radio currently lives (drives the warnings)
  const [radioAllocation, setRadioAllocation] = useState(null);
  // Allocation of the radio already on the site — a ref, so formik's onSubmit
  // closure cannot read a stale one.
  const currentRadioAllocationRef = useRef(null);
  // State for the warning dialog (shown before confirmation dialog)
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [pendingWarningAction, setPendingWarningAction] = useState(null);

  const isSuperUser = user.get('is_superuser', false);
  const isStaff = user.get('is_staff', false);
  const isTechSupport = isSuperUser || determineIsTechSupport(capabilities.groups ?? []);
  const customerId = site?.owner?.customer?.id ?? null;
  const customerName = site?.owner?.customer?.name ?? customerTerm.toLowerCase();

  const buildSwapWarnings = () =>
    buildRadioSwapWarnings({
      allocation: radioAllocation,
      currentAllocation: currentRadioAllocationRef.current,
      hasExistingRadio,
      targetCustomerId: customerId,
      targetCustomerName: customerName,
      targetSiteId: siteId ?? 0,
      isStaff,
      isSuperUser,
      customerTerm,
      t,
    });

  // Build remove warnings (delete button — only the current radio)
  const buildRemoveWarnings = () => {
    const warnings = [];

    const prepaid = currentRadioAllocationRef.current?.prepaid;
    if (prepaid) {
      warnings.push({
        severity: 'info',
        message: t('sites.infoPrepaidRemoveOnly', {
          endDate: prepaid.end_date,
          ownerName: prepaid.owner_name || t('general.unknown'),
        }),
      });
    }

    return warnings;
  };

  const formik = useFormik({
    enableReinitialize: false,
    initialValues: {
      device_type: 'relay_controller',
      iccid: iccid,
      radio: unitRadio,
      ip_address: ipAddress,
      radio_action: 'inventory',
    },
    validationSchema: yup.object().shape({
      device_type: yup.string().min(1).required('Device Type must be selected'),
      ip_address: yup.string().ipAddress(),
      iccid: yup.string().iccidOrMAC().required('ICCID/MAC is required'),
      radio: yup.number().nullable(),
      radio_action: yup.string(),
    }),
    onSubmit: values => {
      // Compute warnings at submit time (reads refs, avoids stale closures)
      const currentSwapWarnings = buildSwapWarnings();

      setPendingSubmitValues(values);

      // Set new radio info from ICCID validation
      const isMAC = validMACAddress.test(values.iccid);
      setNewRadioInfo({
        iccid: isMAC ? null : values.iccid,
        mac_address: isMAC ? values.iccid : null,
        ip_address: values.ip_address,
      });

      if (hasExistingRadio) {
        // Swap flow: set current radio info
        setCurrentRadioInfo({
          iccid: unitIccid,
          mac_address: unitMac,
          ip_address: unit.radio?.ip_address ?? null,
        });

        if (currentSwapWarnings.length > 0) {
          // Show info/warning dialog first
          setPendingWarningAction('swap');
          setShowWarningDialog(true);
        } else {
          // No warnings, go straight to confirmation dialog
          setShowConfirmDialog(true);
        }
      } else {
        // No existing radio (add flow)
        if (currentSwapWarnings.length > 0) {
          // Show warning dialog, then submit directly on OK (no inventory/retire choice needed)
          setPendingWarningAction('add');
          setShowWarningDialog(true);
        } else {
          // No warnings, submit directly
          // eslint-disable-next-line no-use-before-define -- formik <-> performActualSubmit form a submit-time cycle
          performActualSubmit(values, 'inventory');
        }
      }
    },
  });

  const performActualSubmit = (values, selectedRadioAction) => {
    // If values is null, we are deleting.
    let { device_type, radio, ip_address, iccid } = values ?? { radio: unitRadio };

    const currentSiteId = siteId ?? 0;
    const validIP = ip_address !== '' ? ip_address : null;

    const isMAC = validMACAddress.test(iccid);

    const createRadioValues = {
      ip_address: validIP,
      iccid: isMAC ? null : iccid,
      mac_address: isMAC ? iccid : null,
    };

    if (isDeleting) {
      // TODO(ENG): radio removal stays on legacy changeSiteDevice thunk — unit/actions only
      // supports the device-SWAP path. Migrate once a delete action is added to the BE.
      const deviceValues = { radio: null, radio_action: selectedRadioAction ?? 'inventory' };
      dispatch(changeSiteDevice(currentSiteId, deviceValues)).then(values => {
        if (!_.has(values, 'error')) {
          dispatch(
            addToast({
              message: t('sites.deviceUnassignedMessage'),
              level: 'success',
              dismissible: true,
            }),
          );
        } else {
          const errorText = parseApiError(values.error, {
            genericMessage: null,
            knownFields: [],
          }).nonFieldMessages.join(', ');
          return dispatch(
            addToast({
              title: t('general.error', 'Error'),
              message: errorText
                ? `${t('sites.failedToUnassignMessage')}: ${errorText}`
                : `${t('sites.failedToUnassignMessage')}.`,
              level: 'error',
              dismissible: true,
            }),
          );
        }
      });
    } else {
      if (!creatingNewRadio) {
        const SUCCESS_STATUSES = ['queued', 'applied'];
        dispatchUnitActions(currentSiteId, [{ key: 'change-device', params: { radio: { type: 'radio', id: radio } } }])
          .then(batchResult => {
            // Inspect each batch entry — a non-success status indicates a per-item error
            // even when the HTTP response was 200/207.
            const failed = Array.isArray(batchResult)
              ? batchResult.filter(entry => !SUCCESS_STATUSES.includes(entry?.status))
              : [];
            if (failed.length > 0) {
              const errorText = failed
                .map(e => e?.error || e?.detail || '')
                .filter(Boolean)
                .join('; ');
              dispatch(
                addToast({
                  title: t('general.error', 'Error'),
                  message: errorText
                    ? `${t('sites.failedToAssignMessage')}: ${errorText}`
                    : `${t('sites.failedToAssignMessage')}.`,
                  level: 'error',
                  dismissible: true,
                }),
              );
              // Leave the dialog open so the user can retry.
              formik.setSubmitting(false);
              return;
            }
            dispatch(
              addToast({
                message: t('sites.deviceAssignedMessage'),
                level: 'success',
                dismissible: true,
              }),
            );
            formik.setSubmitting(false);
            onClose();
          })
          .catch(err => {
            // A batch where every entry failed answers 400 with the bare per-entry
            // error list, so the reason lives on the entries, not on a `detail`.
            const body = err?.response?.data;
            const errorText = Array.isArray(body)
              ? body
                  .map(entry => entry?.detail || entry?.error || '')
                  .filter(Boolean)
                  .join('; ')
              : _.get(err, ['response', 'data', 'detail'], '');
            dispatch(
              addToast({
                title: t('general.error', 'Error'),
                message: errorText
                  ? `${t('sites.failedToAssignMessage')}: ${errorText}`
                  : `${t('sites.failedToAssignMessage')}.`,
                level: 'error',
                dismissible: true,
              }),
            );
            formik.setSubmitting(false);
          });
        // onClose/setSubmitting handled inside the async chain above.
        return;
      } else {
        // TODO(ENG): create-then-assign flow stays on legacy createRadio + changeSiteDevice thunks
        // — unit/actions only supports swapping an existing radio by id. Migrate once the BE
        // exposes a create-and-assign action.
        dispatch(createRadio(createRadioValues)).then(res => {
          if (!_.has(res, 'error') && res.radios.first().get('id', 0) != 0) {
            const newDeviceValues = { device_type, radio: res.radios.first().get('id', 0) };
            return dispatch(changeSiteDevice(currentSiteId, newDeviceValues)).then(values => {
              if (!_.has(values, 'error')) {
                dispatch(
                  addToast({
                    message: t('sites.deviceAssignedMessage'),
                    level: 'success',
                    dismissible: true,
                  }),
                );
              } else {
                const errorText = parseApiError(values.error, {
                  genericMessage: null,
                  knownFields: [],
                }).nonFieldMessages.join(', ');
                return dispatch(
                  addToast({
                    title: t('general.error', 'Error'),
                    message: errorText
                      ? `${t('sites.failedToAssignMessage')}: ${errorText}`
                      : `${t('sites.failedToAssignMessage')}.`,
                    level: 'error',
                    dismissible: true,
                  }),
                );
              }
            });
          }
        });
      }

      if (unitStatus !== 'Active') {
        dispatch(
          addToast({
            message: t('sites.deviceAssignedActivationMessage'),
            level: 'success',
            dismissible: true,
          }),
        );
      }
    }

    formik.setSubmitting(false);
    onClose();
  };

  const handleConfirmRadioChange = selectedRadioAction => {
    setShowConfirmDialog(false);
    performActualSubmit(pendingSubmitValues, selectedRadioAction);
    setIsDeleting(false);
  };

  const handleCancelConfirmDialog = () => {
    setShowConfirmDialog(false);
    setPendingSubmitValues(null);
    formik.setSubmitting(false);
  };

  const handleWarningConfirm = () => {
    setShowWarningDialog(false);
    if (pendingWarningAction === 'swap' || pendingWarningAction === 'delete') {
      // Proceed to confirmation dialog (inventory/retire choice)
      setShowConfirmDialog(true);
    } else if (pendingWarningAction === 'add') {
      // No existing radio, submit directly
      performActualSubmit(pendingSubmitValues, 'inventory');
    }
    setPendingWarningAction(null);
  };

  const handleWarningCancel = () => {
    setShowWarningDialog(false);
    setPendingWarningAction(null);
    setIsDeleting(false);
    formik.setSubmitting(false);
  };

  const validateICCID = (iccid, { isCurrentRadio = false } = {}) => {
    if (iccid == null || iccid === '') {
      return false;
    }

    const isMAC = validMACAddress.test(iccid);

    setICCIDValidated(false);
    setCreatingNewRadio(false);
    setLoading(true);
    setFetchingIP(!adminOverrideIP);

    return resolveRadios([iccid])
      .then(resolveRes => {
        const match = findResolveMatch(resolveRes, iccid);
        const radioRef = match?.radio ?? null;

        if (radioRef == null) {
          // Valid ICCID/MAC with no Radio row that this user may create
          // (resolve `creatable`) → create-and-assign; anything else is
          // genuinely unrecognized.
          if (match?.creatable) {
            return CREATE_NEW_RADIO;
          }
          formik.setFieldError('iccid', 'Unable to find radio with this ICCID/MAC');
          return null;
        }

        return fetchRadio(radioRef.id);
      })
      .then(radioDetail => {
        if (radioDetail === CREATE_NEW_RADIO) {
          // Restore the legacy lookup `id: null` behavior: a valid, uncreated
          // ICCID/MAC drops into create-new mode and submit creates + assigns it.
          setCreatingNewRadio(true);
          setICCID(iccid);
          setICCIDInfo(t('sites.newRadioOnSubmit', 'New radio — submit to create and assign it.'));
          if (!adminOverrideIP) {
            setIPAddress('');
            formik.setFieldValue('ip_address', '');
          }
          return true;
        }

        if (radioDetail == null) {
          return false;
        }

        const allocation = deriveRadioAllocation(radioDetail);

        setRadioAllocation(allocation);
        if (isCurrentRadio) {
          currentRadioAllocationRef.current = allocation;
        }

        const resolvedIccid = allocation.iccid;
        const resolvedMac = allocation.macAddress;
        const ip = allocation.ipAddress;

        if (
          allocation.site != null &&
          (resolvedIccid === iccid || resolvedMac === iccid.replace('-', '').replace(':', '').toLowerCase())
        ) {
          if (allocation.site.id === (siteId ?? 0)) {
            setICCID(isMAC ? resolvedMac : resolvedIccid);
            setICCIDInfo(t('sites.currentlyOnSite'));
            setUnitRadio(allocation.radioId);
            setIPAddress(ip || '');
            formik.setFieldValue('ip_address', ip || '');
            return true;
          }
        }

        // Only a superuser may take a radio off another site: check_for_duplicate_radio
        // refuses the move for anyone else, so warning and proceeding would promise a
        // save the backend will reject.
        if (allocation.site != null && allocation.isOnSite && !isSuperUser) {
          formik.setFieldError('iccid', 'ICCID in use by another customer or site');
          return false;
        }

        // A radio held by a group belongs to that group whether or not it is billed
        // there — an unbilled radio in inventory still has an owner to name.
        const owningGroup = allocation.billedSiteGroup ?? allocation.siteGroup;
        if (owningGroup != null) {
          formik.setFieldValue('iccid', isMAC ? resolvedMac : resolvedIccid);
          setICCIDInfo(
            Number(siteGroupId) === Number(owningGroup.id) ? (
              t(
                'sites.radioFoundThisGroup',
                "Radio Found in this {{term}}'s billing records. Click submit to assign it.",
                {
                  term: siteGroupTerm.singular.toLowerCase(),
                },
              )
            ) : (
              <div>
                <div style={{ color: orange[500] }}>
                  {' '}
                  {t('sites.radioFoundOtherGroup', "Warning: Radio Found in another {{term}}'s billing records.", {
                    term: siteGroupTerm.singular.toLowerCase(),
                  })}
                </div>
                <div>
                  {t(
                    'sites.radioAssignThisGroup',
                    "You can still click submit to assign it to this {{term}}'s billing records",
                    {
                      term: siteGroupTerm.singular.toLowerCase(),
                    },
                  )}
                </div>
              </div>
            ),
          );
          if (!adminOverrideIP) {
            setIPAddress(ip || '');
            formik.setFieldValue('ip_address', ip || '');
          }
          setICCIDSiteGroupName(owningGroup.name + (owningGroup.description ? '. ' + owningGroup.description : ''));
          setICCIDCustomerName(allocation.ownerCustomer?.name ?? null);
          setUnitRadio(allocation.radioId);
          return true;
        }

        formik.setFieldValue('iccid', iccid);
        setUnitRadio(allocation.radioId);
        if (!adminOverrideIP) {
          setIPAddress(ip || '');
        }
        setICCIDInfo(t('sites.unbilledRadioFound', 'Unbilled radio found — submit to assign it.'));
        return true;
      })
      .then(res => {
        if (isMAC) {
          setICCIDValidated(true);
        }
        if (res && !isMAC) {
          setICCIDValidated(true);
          dispatch(fetchKoreSubscriptionByICCID(iccid)).then(res => {
            const sub = res.subscriptions.toList().toJS()[0];
            setUnitStatus(sub.state);
            // Only update IP if subscription has one AND it's different from current
            if (sub.ip_address && sub.ip_address != ipAddress && !adminOverrideIP) {
              setIPAddress(sub.ip_address);
              formik.setFieldValue('ip_address', sub.ip_address);
              setIPAddressChanged(true);
            }
          });
        }
      })
      .catch(() => {
        formik.setFieldError('iccid', t('sites.radioLookupFailed', 'Could not look up this radio. Try again.'));
        return false;
      })
      .finally(() => {
        setLoading(false);
        setFetchingIP(false);
      });
  };

  const handleClose = () => {
    onClose();
    formik.resetForm();
  };

  const handleSubmit = () => {
    formik.handleSubmit();
  };

  const scan = async () => {
    const { barcodes } = await BarcodeScanner.scan({
      formats: [BarcodeFormat.QrCode],
    });
    const qr = barcodes[0].rawValue;
    setICCID(qr);
  };

  useEffect(() => {
    if (triggerScanOnOpen) {
      scan();
    }
  }, [triggerScanOnOpen]);

  useEffect(() => {
    // Just determine IP address type, don't sync with formik
    // (formik is set directly where ipAddress is updated)
    const detectedType = determineIPAddressType(ipAddress);
    setIPAddressType(detectedType);
  }, [ipAddress]);

  useEffect(() => {
    // Guard: only search radio locations when the dialog is actually open.
    // Without this guard the search fires on mount (dialog is always-mounted
    // in UnitCard) and duplicates the call on every iccid change regardless
    // of whether the user has opened the dialog.
    if (!open) return;

    const isMounted = { current: true };

    const loadOptions = async () => {
      const isMAC = validMACAddress.test(iccid);

      formik.setFieldValue('iccid', iccid);
      setICCIDValidated(false);
      setICCIDInfo(null);
      setICCIDCustomerName(null);
      setICCIDSiteGroupName(null);
      if (siteGroupId != null) {
        const locationParams = {
          site_group: siteGroupId,
          site__isnull: true,
        };
        if (isMAC) {
          locationParams.radio__mac_address__icontains = iccid.replace('-', '').replace(':', '').toLowerCase();
        } else {
          locationParams.radio__iccid__contains = iccid;
        }
        try {
          const response = await dispatch(loadRadioLocations(locationParams)).unwrap();
          if (isMounted.current) {
            setICCIDOptions(
              response.results.map(i => {
                const iccid = _.get(i, ['radio', 'iccid'], null);
                const macAddress = _.get(i, ['radio', 'mac_address'], null);
                if (iccid == null || macAddress != null) {
                  return { label: macAddress, value: macAddress };
                }
                return { label: iccid, value: iccid };
              }),
            );
          }
        } catch (error) {
          if (isMounted.current) {
            console.error('Failed to load radio locations:', error);
          }
        }
      }
    };

    loadOptions();

    // Cleanup function to set isMounted to false on unmount
    return () => {
      isMounted.current = false;
    };
  }, [iccid, open]);

  // Re-initialize state from unit when the dialog opens
  useEffect(() => {
    if (open) {
      const currentIccid = unit.radio?.iccid ?? unit.radio?.mac_address ?? '';
      const currentRadioId = unit.radio?.id ?? null;
      const currentIP = unit.radio?.ip_address ?? '';
      setICCID(currentIccid);
      setUnitRadio(currentRadioId);
      setIPAddress(currentIP);
      setIPAddressChanged(false);
      setCreatingNewRadio(false);
      setICCIDValidated(false);
      setICCIDInfo(null);
      setICCIDSiteGroupName(null);
      setICCIDCustomerName(null);
      setRadioAllocation(null);
      currentRadioAllocationRef.current = null;
      setIsDeleting(false);
      setShowConfirmDialog(false);
      setShowWarningDialog(false);
      setPendingWarningAction(null);
      setPendingSubmitValues(null);
      setCurrentRadioInfo(null);
      setNewRadioInfo(null);
      setAdminOverrideIP(false);
      setTitle(edit ? t('sites.changeUnit') : t('sites.addDevice'));
      formik.resetForm({
        values: {
          device_type: 'relay_controller',
          iccid: currentIccid,
          radio: currentRadioId,
          ip_address: currentIP,
          radio_action: 'inventory',
        },
      });
      // Validate the current ICCID and store as current radio lookup
      if (currentIccid) {
        validateICCID(currentIccid, { isCurrentRadio: true });
      }
    }
  }, [open]);

  useEffect(() => {
    if (unitRadio) {
      formik.setFieldValue('radio', unitRadio);
    }
  }, [unitRadio]);

  const isBusy = loading || fetchingIP || formik.isSubmitting;

  const extraActions = edit && [
    <Button
      key="delete"
      color="error"
      disabled={isBusy || unitRadio == null}
      onClick={() => {
        setIsDeleting(true);
        setCurrentRadioInfo({
          iccid: unitIccid,
          mac_address: unitMac,
          ip_address: unit.radio?.ip_address ?? null,
        });
        setNewRadioInfo(null);
        setPendingSubmitValues(null);
        const currentRemoveWarnings = buildRemoveWarnings();
        if (currentRemoveWarnings.length > 0) {
          // Step 1: Show warning dialog first
          setPendingWarningAction('delete');
          setShowWarningDialog(true);
        } else {
          // No warnings, go straight to confirmation dialog
          setShowConfirmDialog(true);
        }
      }}
    >
      {t('actions.delete')}
    </Button>,
    isNativePlatform && (
      <Button key="scan" onClick={scan}>
        {t('actions.scan')}
      </Button>
    ),
  ];

  // Check if trying to assign the same ICCID/MAC that's already attached
  const currentIccidOrMac = unitIccid || unitMac;
  const enteredIccid = formik.values.iccid;
  const isSameRadio = edit && currentIccidOrMac != null && enteredIccid === currentIccidOrMac && iccidValidated;

  // Only allow submission if ICCID is validated AND (not edit mode OR different radio selected)
  // Only allow submission if ICCID is validated AND (not edit mode OR different radio selected)
  const canSubmitForm = iccidValidated && !isSameRadio && !(isBusy || Object.keys(formik.errors).length > 0);

  return (
    <ResponsiveDialog
      title={title}
      open={open}
      canSubmit={canSubmitForm}
      onSubmit={handleSubmit}
      onClose={handleClose}
      extraActions={extraActions}
      sx={{ '& .MuiDialogContent-root > *:not(:last-child)': { mb: 3 } }}
    >
      <input type="hidden" value={formik.values.radio} />
      <Box>
        <Autocomplete
          freeSolo
          required
          disabled={isBusy}
          id="iccid-selection"
          options={iccidOptions ?? []}
          inputValue={formik.values.iccid ?? ''}
          isOptionEqualToValue={(option, value) => {
            return option.value === value.value;
          }}
          getOptionLabel={option => {
            return option ? option.label : '';
          }}
          onChange={(e, opt) => {
            setICCID(opt.value);
            formik.setFieldValue('iccid', opt.value);
          }}
          onInputChange={(event, value) => {
            setICCID(value);
            formik.setFieldValue('iccid', value);
          }}
          onBlur={formik.handleBlur}
          renderInput={params => {
            return (
              <TextField
                {...params}
                fullWidth
                name="iccid"
                label={t('sites.iccidMac', 'ICCID/MAC')}
                size="small"
                InputProps={{
                  ...params.InputProps,
                  type: 'search',
                  startAdornment: (
                    <InputAdornment position="start">
                      <SensorsIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {iccidValidated ? (
                        <CheckCircleIcon sx={{ fill: green[500] }} />
                      ) : (
                        <Button
                          size="small"
                          color="primary"
                          disabled={formik.errors.iccid != null || isBusy}
                          onClick={() => validateICCID(formik.values.iccid)}
                        >
                          {fetchingIP ? t('sites.validating', 'Validating') : t('sites.validate', 'Validate')}
                        </Button>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            );
          }}
        />

        <FormHelperText>{iccidInfo ?? formik.errors.iccid ?? null}</FormHelperText>
        {isSameRadio && (
          <FormHelperText error>
            {t('validation.radioAlreadyAttached', 'This radio is already attached to this unit')}
          </FormHelperText>
        )}
      </Box>
      {isTechSupport ? (
        <Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', '> *:not(:last-child)': { mr: 1 } }}>
            <TextField
              disabled={!adminOverrideIP}
              fullWidth
              name="ip_address"
              label={t('sites.ipAddress', 'IP Address')}
              size="small"
              error={formik.touched.ip_address && formik.errors.ip_address != null}
              value={formik.values.ip_address}
              onChange={e => {
                e.target.value = e.target.value.trim().toUpperCase();
                if (e.target.value === '' || allowedIPAddress.test(e.target.value)) {
                  setIPAddress(e.target.value);
                  formik.handleChange(e);
                }
              }}
              onBlur={formik.handleBlur}
              inputProps={{
                pattern: `[${allowedIPAddressCharacters}]`,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {fetchingIP ? <CircularProgress size={20} /> : <DnsIcon />}
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {!fetchingIP && (
                      <Chip
                        label={
                          unitStatus === 'Active'
                            ? ipAddressType || 'IPv4/IPv6'
                            : unitStatus && unitStatus !== '' && unitStatus !== 'Unknown'
                              ? unitStatus
                              : ipAddressType || 'IPv4/IPv6'
                        }
                        color="primary"
                        size="small"
                      />
                    )}
                  </InputAdornment>
                ),
              }}
              helperText={formik.touched.ip_address ? formik.errors.ip_address : null}
            />
            <ToggleButton
              value="check"
              size="small"
              variant="standard"
              selected={adminOverrideIP}
              onChange={() => setAdminOverrideIP(!adminOverrideIP)}
            >
              {t('sites.override', 'Override')}
            </ToggleButton>
          </Box>
          <FormHelperText>{t('sites.superUsersOnlyField', 'Only Super Users can view this field.')}</FormHelperText>
        </Box>
      ) : (
        <Fragment>
          {isTechSupport && <p>{formik.values.ip_address}</p>}
          <input type="hidden" name="ip_address" value={formik.values.ip_address} />
        </Fragment>
      )}
      <RadioWarningDialog
        open={showWarningDialog}
        onClose={handleWarningCancel}
        onConfirm={handleWarningConfirm}
        warnings={pendingWarningAction === 'delete' ? buildRemoveWarnings() : buildSwapWarnings()}
      />
      <RadioChangeConfirmationDialog
        open={showConfirmDialog}
        onClose={handleCancelConfirmDialog}
        onConfirm={handleConfirmRadioChange}
        currentRadio={currentRadioInfo}
        newRadio={newRadioInfo}
        radioAction={radioAction}
        setRadioAction={setRadioAction}
      />
    </ResponsiveDialog>
  );
}

export default ChangeUnitDialog;
